import { ComponentPropsWithRef, forwardRef } from "react";
import { Form } from "@remix-run/react";
import clsx from "clsx";

import { Button } from "~/components/Button";
import { BlockLoader } from "~/components/Loader";
import { Markdown } from "~/components/Markdown";
import { useScrollIntoView } from "~/hooks/useScrollIntoView";
import { usePendingNavigation } from "~/hooks/useShowSpinner";
import { MeetingTypeIndicator } from "~/modules/meeting-type/MeetingType";
import { SubjectLoaderData } from "~/routes/$enterprise+/plugin+/$sessionId.schedule.subject";
import { useMeetingTypes } from "~/routes/api+/enterprises.$enterprise.meeting-types";

interface SubjectContainerProps extends ComponentPropsWithRef<"section"> {
  category: ArrayElement<SubjectLoaderData>;
}

export const SubjectsContainer = forwardRef<HTMLElement, SubjectContainerProps>(
  ({ category, ...props }, ref) => {
    const scrollToRef = useScrollIntoView<HTMLDivElement>();

    return (
      <section
        {...props}
        className="col-span-full flex w-full flex-col space-y-sm"
        data-component="SubjectsContainer"
        ref={scrollToRef}
      >
        {category.subjects.map((subject) => (
          <Subject category={category} key={subject.id} subject={subject} />
        ))}
      </section>
    );
  },
);

interface SubjectProps extends ComponentPropsWithRef<"section"> {
  subject: ArrayElement<ArrayElement<SubjectLoaderData>["subjects"]>;
  category: ArrayElement<SubjectLoaderData>;
}

const NAME = "subjectId";
const Subject = forwardRef<HTMLElement, SubjectProps>(function Subject(
  { category, subject, ...props },
  ref,
) {
  const { description, title } = subject;
  const meetingTypes = useMeetingTypes({ subjectId: subject.id.toString() });
  const { isLoading, isLoadingForValue } = usePendingNavigation(NAME, subject.id.toString());

  return (
    <Form method="post">
      <input name={NAME} type="hidden" value={subject.id.toString()}></input>
      <Button
        aria-label={`Select subject ${title}`}
        className="relative w-full text-left"
        data-component="Subject"
        data-external-id={subject.externalId}
        data-subject-group-external-id={category.externalId}
        isDisabled={isLoading}
        type="submit"
      >
        {isLoadingForValue && <BlockLoader />}
        <section
          data-external-id={subject.externalId}
          {...props}
          className={clsx(
            "flex flex-col",
            "rounded border",
            "space-y-sm p-sm",
            //TODO change the hover background to very light brand color
            "bg-white hover:bg-gray-100",
            "transition-colors",
          )}
          data-component="SubjectContent"
          ref={ref}
        >
          <section className="flex flex-row items-center justify-between gap-sm">
            <div className="flex flex-1 flex-wrap items-center justify-between gap-sm">
              <div className="flex flex-col gap-xs">
                <section
                  className="text-sm font-normal text-secondary"
                  data-component="CategoryTitle"
                >
                  {category.name}
                </section>
                <section
                  className="max-w-[500px] text-base font-semibold"
                  data-component="SubjectTitle"
                >
                  {title}
                </section>
              </div>

              {meetingTypes && meetingTypes.length > 0 && (
                <div className="flex items-center gap-3xs" data-component="SubjectMeetingTypes">
                  {meetingTypes.map((meetingType) => (
                    <MeetingTypeIndicator key={meetingType} meetingType={meetingType} />
                  ))}
                </div>
              )}
            </div>
          </section>
          {description && (
            <span className="max-w-[500px] text-sm" data-component="SubjectDescription">
              <Markdown text={description} />
            </span>
          )}
        </section>
      </Button>
    </Form>
  );
});
